import React, { useImperativeHandle, useState } from "react";
import { Form } from "react-final-form";
import { FORM_ERROR } from "final-form";
import { ConsentBannerUSTextRow } from "../../organisms/settings/rows/consentBanner/ConsentBannerUSTextRow";
import { ConsentBannerUSButtonRow } from "../../organisms/settings/rows/consentBanner/ConsentBannerUSButtonRow";
import { Done as DoneIcon } from "@mui/icons-material";
import { requiredString } from "../../../util/validation";
import { Link, Tooltip } from "@mui/material";
import { usePrimaryOrganizationId } from "../../../hooks";
import { useCMPCustomization, usePrivacyCenter } from "../../../hooks/usePrivacyCenter";
import { NetworkRequestState } from "../../../../common/models";
import {
  CMPSettingsDto,
  BannerCopyVariant,
  ConsentManagementVariant,
  ConsentRequiredMode,
  CustomConsentManagementVariant,
  InformationalBannerDismissalStyle,
} from "../../../../common/service/server/dto/CMPSettingsDto";
import { PrivacyCenterDto } from "../../../../common/service/server/dto/PrivacyCenterDto";
import { ConsentManagementVariantCustomRow } from "../../organisms/settings/rows/consentBanner/ConsentManagementVariantCustomRow";
import { ConsentBannerBackgroundColorRow } from "../../organisms/settings/rows/consentBanner/ConsentBannerBackgroundColorRow";
import {
  ConsentBannerIconColorRow,
  ICON_PRIMARY_COLOR_DEFAULT,
  ICON_SECONDARY_COLOR_DEFAULT,
} from "../../organisms/settings/rows/consentBanner/ConsentBannerIconColorRow";
import { ConsentBannerUSTitleRow } from "../../organisms/settings/rows/consentBanner/ConsentBannerUSTitleRow";
import { ConsentBannerUSDismissalRow } from "../../organisms/settings/rows/consentBanner/ConsentBannerUSDismissalRow";
import { OrganizationDto } from "../../../../common/service/server/dto/OrganizationDto";
import { SettingRow } from "../../organisms/settings/rows/SettingRow";
import clsx from "clsx";
import { ProcessingRegion } from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { ZeroStateHero } from "../../organisms/zerostate/ZeroStateHero";
import { Loading } from "../../../../common/components/Loading";
import { sleep } from "../../../util/sleep";
import { ConsentBannerPreviewURLRow } from "../../organisms/settings/rows/consentBanner/ConsentBannerPreviewURLRow";
import { ConsentBannerScreenPositionRow } from "../../organisms/settings/rows/consentBanner/ConsentBannerScreenPositionRow";
import { useCookieScanResult } from "../../../hooks/useCookieScanResult";
import { Callout, CalloutVariant } from "../../Callout";
import { LightBulbIcon } from "@heroicons/react/24/solid";
import { RouteHelpers, Routes } from "../../../root/routes";
import { ConsentBannerConsentRequiredRow } from "../../organisms/settings/rows/consentBanner/ConsentBannerConsentRequiredRow";
import { ConsentBannerAllowCSSOverridesRow } from "../../organisms/settings/rows/consentBanner/ConsentBannerAllowCSSOverridesRow";
import {
  AlertCard,
  Button as AquiloButton,
  CopyableText,
  Toggle,
  ToolTip,
  useSnackBar,
} from "@truevault/aquilo";
import { ConsentManagementVariantRow } from "../../organisms/settings/rows/consentBanner/ConsentManagementVariantRow";
import { Iso3166Region } from "../../../../common/service/server/Types";
import { ConfirmDialog } from "../../dialog/ConfirmDialog";
import { Text } from "../../../../common/components/input/Text";
import { ConsentBannerCopyOverrideRow } from "../../organisms/settings/rows/consentBanner/ConsentBannerCopyOverrideRow";
import { isStagingEnvironment } from "../../../util/features";
import { UnpublishedChangesBanner } from "../../organisms/callouts/UnpublishedChangesBanner";
import { ConsentBannerOptInButtonTextRow } from "../../organisms/settings/rows/consentBanner/ConsentBannerOptInButtonTextRow";

interface ConsentBannerSettingsProps extends CMPSettingsDto {
  onUpdateCMPCustomization: (newCMPCustomization: CMPSettingsDto) => Promise<void>;
  saveSuccess: boolean;
  organization: OrganizationDto;
  privacyCenterURL:
    | string
    | undefined /* The URL of the privacy center - can guess a good default preview URL from here. */;
  privacyCenterPublicId: string /* The public ID of the privacy center, used to generate the staging URL. */;
  isConfirmedCustomizableCMPVersion: boolean;
}

export interface ConsentBannerSettingsHandle {
  /** Submits the form. Returns true if submission was successful, false if there were validation errors. */
  submitForm: () => Promise<boolean>;
}

export interface OverrideableRegion {
  region: Iso3166Region;
  label: string;
  abbreviation: string;
  isUSP: boolean;
  isNonUSP: boolean;
  isChildrenConsent?: boolean;
  dataCollectionAge?: number;
}

// reference the constant USP_STATES=['VA', 'CO', 'CT', 'UT', 'TX', 'OR', 'MT', 'FL', 'IA', 'DE', 'NH', 'NE', 'NJ', 'MD', 'NV', ...] from
// the CMP and keep this in sync so we can show each state in the appropriate "See States" hover list
export const OVERRIDEABLE_REGIONS: OverrideableRegion[] = [
  { region: "US-AL", label: "Alabama", abbreviation: "AL", isUSP: false, isNonUSP: true },
  { region: "US-AK", label: "Alaska", abbreviation: "AK", isUSP: false, isNonUSP: true },
  { region: "US-AZ", label: "Arizona", abbreviation: "AZ", isUSP: false, isNonUSP: true },
  { region: "US-AR", label: "Arkansas", abbreviation: "AR", isUSP: false, isNonUSP: true },
  {
    region: "US-CA",
    label: "California",
    abbreviation: "CA",
    isUSP: false,
    isNonUSP: false,
    isChildrenConsent: true,
    dataCollectionAge: 16,
  },
  {
    region: "US-CO",
    label: "Colorado",
    abbreviation: "CO",
    isUSP: true,
    isNonUSP: false,
    isChildrenConsent: true,
    dataCollectionAge: 18,
  },
  {
    region: "US-CT",
    label: "Connecticut",
    abbreviation: "CT",
    isUSP: true,
    isNonUSP: false,
    isChildrenConsent: true,
    dataCollectionAge: 18,
  },
  {
    region: "US-DE",
    label: "Delaware",
    abbreviation: "DE",
    isUSP: true,
    isNonUSP: false,
    isChildrenConsent: true,
    dataCollectionAge: 18,
  },
  {
    region: "US-DC",
    label: "District of Columbia",
    abbreviation: "DC",
    isUSP: false,
    isNonUSP: true,
  },
  { region: "US-FL", label: "Florida", abbreviation: "FL", isUSP: true, isNonUSP: false },
  { region: "US-GA", label: "Georgia", abbreviation: "GA", isUSP: false, isNonUSP: true },
  { region: "US-HI", label: "Hawaii", abbreviation: "HI", isUSP: false, isNonUSP: true },
  { region: "US-ID", label: "Idaho", abbreviation: "ID", isUSP: false, isNonUSP: true },
  { region: "US-IL", label: "Illinois", abbreviation: "IL", isUSP: false, isNonUSP: true },
  { region: "US-IN", label: "Indiana", abbreviation: "IN", isUSP: true, isNonUSP: false },
  { region: "US-IA", label: "Iowa", abbreviation: "IA", isUSP: true, isNonUSP: false },
  { region: "US-KS", label: "Kansas", abbreviation: "KS", isUSP: false, isNonUSP: true },
  { region: "US-KY", label: "Kentucky", abbreviation: "KY", isUSP: true, isNonUSP: false },
  { region: "US-LA", label: "Louisiana", abbreviation: "LA", isUSP: false, isNonUSP: true },
  { region: "US-ME", label: "Maine", abbreviation: "ME", isUSP: false, isNonUSP: true },
  {
    region: "US-MD",
    label: "Maryland",
    abbreviation: "MD",
    isUSP: true,
    isNonUSP: false,
    isChildrenConsent: true,
    dataCollectionAge: 18,
  },
  { region: "US-MA", label: "Massachusetts", abbreviation: "MA", isUSP: false, isNonUSP: true },
  { region: "US-MI", label: "Michigan", abbreviation: "MI", isUSP: false, isNonUSP: true },
  { region: "US-MN", label: "Minnesota", abbreviation: "MN", isUSP: true, isNonUSP: false },
  { region: "US-MS", label: "Mississippi", abbreviation: "MS", isUSP: false, isNonUSP: true },
  { region: "US-MO", label: "Missouri", abbreviation: "MO", isUSP: false, isNonUSP: true },
  {
    region: "US-MT",
    label: "Montana",
    abbreviation: "MT",
    isUSP: true,
    isNonUSP: false,
    isChildrenConsent: true,
    dataCollectionAge: 18,
  },
  { region: "US-NE", label: "Nebraska", abbreviation: "NE", isUSP: true, isNonUSP: false },
  { region: "US-NV", label: "Nevada", abbreviation: "NV", isUSP: true, isNonUSP: false },
  {
    region: "US-NH",
    label: "New Hampshire",
    abbreviation: "NH",
    isUSP: true,
    isNonUSP: false,
    isChildrenConsent: true,
    dataCollectionAge: 16,
  },
  {
    region: "US-NJ",
    label: "New Jersey",
    abbreviation: "NJ",
    isUSP: true,
    isNonUSP: false,
    isChildrenConsent: true,
    dataCollectionAge: 17,
  },
  { region: "US-NM", label: "New Mexico", abbreviation: "NM", isUSP: false, isNonUSP: true },
  { region: "US-NY", label: "New York", abbreviation: "NY", isUSP: false, isNonUSP: true },
  { region: "US-NC", label: "North Carolina", abbreviation: "NC", isUSP: false, isNonUSP: true },
  { region: "US-ND", label: "North Dakota", abbreviation: "ND", isUSP: false, isNonUSP: true },
  { region: "US-OH", label: "Ohio", abbreviation: "OH", isUSP: false, isNonUSP: true },
  { region: "US-OK", label: "Oklahoma", abbreviation: "OK", isUSP: false, isNonUSP: true },
  { region: "US-OR", label: "Oregon", abbreviation: "OR", isUSP: true, isNonUSP: false },
  { region: "US-PA", label: "Pennsylvania", abbreviation: "PA", isUSP: false, isNonUSP: true },
  { region: "US-RI", label: "Rhode Island", abbreviation: "RI", isUSP: true, isNonUSP: false },
  { region: "US-SC", label: "South Carolina", abbreviation: "SC", isUSP: false, isNonUSP: true },
  { region: "US-SD", label: "South Dakota", abbreviation: "SD", isUSP: false, isNonUSP: true },
  { region: "US-TN", label: "Tennessee", abbreviation: "TN", isUSP: true, isNonUSP: false },
  { region: "US-TX", label: "Texas", abbreviation: "TX", isUSP: true, isNonUSP: false },
  { region: "US-UT", label: "Utah", abbreviation: "UT", isUSP: true, isNonUSP: false },
  { region: "US-VT", label: "Vermont", abbreviation: "VT", isUSP: false, isNonUSP: true },
  { region: "US-VA", label: "Virginia", abbreviation: "VA", isUSP: true, isNonUSP: false },
  { region: "US-WA", label: "Washington", abbreviation: "WA", isUSP: false, isNonUSP: true },
  { region: "US-WV", label: "West Virginia", abbreviation: "WV", isUSP: false, isNonUSP: true },
  { region: "US-WI", label: "Wisconsin", abbreviation: "WI", isUSP: false, isNonUSP: true },
  { region: "US-WY", label: "Wyoming", abbreviation: "WY", isUSP: false, isNonUSP: true },
];

const NoCookiesCallout = () => {
  return (
    <Callout variant={CalloutVariant.Purple}>
      <div style={{ display: "flex", alignItems: "center" }}>
        <div style={{ marginRight: "0.5em" }}>
          <LightBulbIcon
            style={{
              width: "1.5em",
              height: "1.5em",
              marginTop: "0.2em",
              color: "#5331c6",
            }}
          />
        </div>

        <div>
          Before configuring your banner, we need to know what cookies you have on your site. Visit
          your <Link href={Routes.cookies.root}>Cookie Scanner</Link> to get started, then return
          here to configure your banner.
        </div>
      </div>
    </Callout>
  );
};

const LOADING_PREVIEW_DELAY_SECONDS = 11; // Delay to allow preview time to publish. Err on side of longer since it would be embarrassing to launch and have it not be there.
export const INFORMATIONAL_BANNER_TITLE_DEFAULT_TEXT = "We Take Privacy Seriously";

export const INFORMATIONAL_BANNER_DEFAULT_TEXT =
  "This site uses cookies and third-party technologies that may be used to track users' interactions with our website, provide communication tools, personalize ads, and more. By continuing to use this site, you are agreeing to the use of these technologies and the processing of your personal information by third parties as described in our %{privacy_request.privacy_policy} and terms of service.";
const INFORMATIONAL_BANNER_DEFAULT_TEXT_LEGACY =
  "This site uses cookies and third-party technologies that may be used to track users' interactions with our website, provide communication tools, personalize ads, and more. By continuing to use this site, you are agreeing to the use of these technologies and the processing of your personal information by third parties as described in our privacy policy and terms of service.";

// These suggested defaults are from the polaris-i18n project.
export const OPT_OUT_BANNER_DEFAULT_TEXT =
  "To provide the best experience for you, we collect data through web cookies and similar technologies. See our %{privacy_request.privacy_policy} or manage choices below.";
export const OPT_IN_BANNER_DEFAULT_TEXT =
  "By clicking Accept, you agree to the use of cookies and tracking technology for personalization, analytics, and advertising. See our %{privacy_request.privacy_policy} for more info.";
const SELLING_SHARING_DEFAULT_TEXT_SUFFIX = "You may %{one_click_opt_out_link} of data sharing.";

const appendSellingSharingSuffix = (defaultText: string, isSellingOrSharing: boolean): string =>
  isSellingOrSharing ? `${defaultText} ${SELLING_SHARING_DEFAULT_TEXT_SUFFIX}` : defaultText;

const isKnownInformationalBannerDefaultText = (text: string): boolean => {
  const normalizedText = text.trim();
  return [INFORMATIONAL_BANNER_DEFAULT_TEXT, INFORMATIONAL_BANNER_DEFAULT_TEXT_LEGACY].includes(
    normalizedText,
  );
};

export const INFORMATIONAL_BANNER_ACCEPT_BUTTON_DEFAULT_TEXT = "Okay";

const FORM_FIELDS_TO_HUMAN_DESCRIPTION = {
  usConsentManagementVariant: "US Banner",
  usConsentManagementVariantCA: "California",
  usConsentManagementVariantUSP: "Privacy States",
  usConsentManagementVariantNonUSP: "Non-Privacy States",
  usConsentManagementVariantMinors: "Children Consent States",
  canadaConsentManagementVariant: "Canada Banner",
  eeaukConsentManagementVariant: "EEA/UK/CH Banner",
  customConsentManagementVariants: "Custom Banner Regions",
  bannerIconColorPrimary: "Icon Primary Color",
  bannerIconColorSecondary: "Icon Secondary Color",
  bannerBackgroundColor: "Banner Background Color",
  bannerScreenPosition: "Banner Screen Position",
  informationalConsentManagementVariant: "US Banner Enabled",
  informationalBannerTitle: "US Banner Title",
  informationalBannerText: "US Banner Text",
  informationalBannerAcceptButtonText: "US Banner Accept Button Text",
  informationalBannerDismissalStyle: "Informational Banner Dismissal",
  bannerTextOptInEn: "Custom Opt-In Banner Text",
  bannerTextOptOutEn: "Custom Opt-Out Banner Text",
  bannerCopyVariant: "Opt-In Banner Button Text",
  gdprConsentManagementEnabled: "Consent Management Enabled",
  previewURL: "Preview URL",
  consentRequired: "Require Consent Choice",
  allowCSSOverrides: "Allow Custom Styles",
};

/**
 * We only track organization privacy center URLs currently, not their actual website URL. That makes it a little tricky
 * to automatically send the user to the org website for a preview. Luckily in most cases the org website is the root of
 * the privacy center URL (which is typically just `privacy.` tacked on to the front). See if we can extract it, and use
 * that value as the default preview URL if so.
 * @param privacyCenterURL The organization's privacy center URL.
 */
const tryExtractWebsiteURLfromPCURL = (privacyCenterURL: string): string | null => {
  try {
    const url = new URL(privacyCenterURL);
    const hostname = url.hostname;

    if (hostname.startsWith("privacy.")) {
      const domain = hostname.replace("privacy.", "");
      return `${domain}`;
    }
    return null;
  } catch (error) {
    return null;
  }
};

export const ConsentBannerSettings = React.forwardRef<
  ConsentBannerSettingsHandle,
  ConsentBannerSettingsProps
>((props, ref) => {
  const { organization } = props;
  const isSellingOrSharing = props.orgInfo?.isSellingOrSharing === true;
  const acceptsVoluntaryRequests = props.orgInfo?.acceptsVoluntaryRequests === true;
  const showOneClickOptOutLink = isSellingOrSharing && acceptsVoluntaryRequests;
  const informationalBannerDefaultText = appendSellingSharingSuffix(
    INFORMATIONAL_BANNER_DEFAULT_TEXT,
    showOneClickOptOutLink,
  );
  const storedInformationalBannerText = props.informationalBannerText?.trim();
  const hasStoredInformationalBannerText = Boolean(storedInformationalBannerText);
  const shouldUseComputedInformationalDefault =
    !hasStoredInformationalBannerText ||
    (showOneClickOptOutLink &&
      isKnownInformationalBannerDefaultText(storedInformationalBannerText));
  const informationalBannerInitialText = shouldUseComputedInformationalDefault
    ? informationalBannerDefaultText
    : props.informationalBannerText;
  const optOutBannerDefaultText = appendSellingSharingSuffix(
    OPT_OUT_BANNER_DEFAULT_TEXT,
    showOneClickOptOutLink,
  );
  const optInBannerDefaultText = appendSellingSharingSuffix(
    OPT_IN_BANNER_DEFAULT_TEXT,
    showOneClickOptOutLink,
  );

  const showGDPRBannerSettings = organization?.featureGdpr;
  const showCanadaBannerSettings = organization?.featurePipeda;
  const showMinorsConsentSettings =
    props.orgInfo?.showMinorsConsentSettingsUnder18 === true ||
    props.orgInfo?.showMinorsConsentSettingsUnder16 === true;
  const showCalifornia = !props.orgInfo?.showMinorsConsentSettingsUnder16; // If collecting from users under 16, CA is one of the Children states.

  // the youngest age we need to worry about for data collection consent (17 to account for New Jersey, if org is collecting under-18 but not under-16).
  const dataCollectionAge =
    props.orgInfo?.showMinorsConsentSettingsUnder16 === true
      ? 16
      : props.orgInfo?.showMinorsConsentSettingsUnder18 === true
      ? 17
      : null;

  // Toggle to show the custom consent management variants. We don't store this toggle's value - just the list of custom
  // variants - so it's stateful on the page (based on if there *are* any custom variants).
  const [showUSOverrides, setShowUSOverrides] = useState<boolean>(
    props.customConsentManagementVariants?.length > 0,
  );

  const [loadingPreview, setLoadingPreview] = useState<boolean>(false);
  const [loadingPreviewProgress, setLoadingPreviewProgress] = useState<string>("0%"); // Make the preview waiting feel faster by showing a percent
  const [previewReady, setPreviewReady] = useState<boolean>(false); // Preview is ready, show a button to launch it. Two effects: additional imperceptible delay to allow for even more time to publish, AND avoids "popup blocked" since it's a direct user click instead of delayed window.open.
  const [bannerPreviewURL, setBannerPreviewURL] = useState<string>(""); // The preview URL we need to shuttle out of the form vals to the preview launch button.

  // Ref to store the form's submit handler so we can expose it to parent components
  const submitHandlerRef = React.useRef<(() => Promise<unknown>) | null>(null);

  useImperativeHandle(ref, () => ({
    submitForm: async () => {
      if (submitHandlerRef.current) {
        const result = await submitHandlerRef.current();
        // If result is undefined/null, submission was successful
        // If result has errors (e.g., FORM_ERROR), submission failed
        return !result;
      }
      return false;
    },
  }));

  const orgId = usePrimaryOrganizationId();
  const [cookieScan] = useCookieScanResult(orgId);
  const hasCookies = cookieScan?.cookies.length > 0;
  const enqueueSnackbar = useSnackBar();

  const publishStaging = async (
    formVals: Partial<CMPSettingsDto> & { consentRequired?: boolean },
  ) => {
    formVals.previewForceGeoCountry = null; // Clear UI Preview values.
    formVals.previewForceGeoRegion = null;
    formVals.isStagingPublish = true; // Set the staging flag so we know to publish to staging.

    // Re-map checkbox (whose value exists only as a form prop) to DTO enum
    formVals.consentRequiredMode = formVals.consentRequired
      ? ConsentRequiredMode.EVERYWHERE
      : ConsentRequiredMode.NONE;

    props.onUpdateCMPCustomization(formVals as CMPSettingsDto);
  };

  const publishPreview = async (
    previewForLocation: ProcessingRegion,
    formVals: Partial<CMPSettingsDto> & { consentRequired?: boolean },
  ) => {
    if (!formVals.previewURL) {
      enqueueSnackbar("No URL set for this privacy center, couldn't launch preview.", {
        variant: "error",
      });
      return;
    }

    setBannerPreviewURL(formVals.previewURL);
    setLoadingPreview(true);

    switch (previewForLocation) {
      case "UNITED_STATES":
        formVals.previewForceGeoCountry = "US";
        formVals.previewForceGeoRegion = "CA";
        break;
      case "EEA_UK":
        formVals.previewForceGeoCountry = "UK";
        formVals.previewForceGeoRegion = "";
        break;
      case "CANADA":
        formVals.previewForceGeoCountry = "CA";
        formVals.previewForceGeoRegion = "";
        break;
    }

    // Re-map checkbox (whose value exists only as a form prop) to DTO enum
    formVals.consentRequiredMode = formVals.consentRequired
      ? ConsentRequiredMode.EVERYWHERE
      : ConsentRequiredMode.NONE;

    props.onUpdateCMPCustomization(formVals as CMPSettingsDto);
    for (let i = 0; i < LOADING_PREVIEW_DELAY_SECONDS; i++) {
      // Publish is pretty fast but not instant. Add some random noise so it's not totally obvious.
      setLoadingPreviewProgress(
        Math.max(
          0,
          Math.round((i * 100) / LOADING_PREVIEW_DELAY_SECONDS) +
            (Math.round(Math.random() * 7) - 3),
        ) + "%",
      );
      await sleep(1000);
    }

    setPreviewReady(true);
  };

  /**
   * The row label and "See States" hover component for the consent rows.
   * @param fieldName The name of the field to map to a label.
   * @param customVariantsUS The custom variants US list to check for overrides to indicate.
   */
  const fieldDescriptionToRowLabelProvider = (
    fieldName: string,
    customVariantsUS?: CustomConsentManagementVariant[],
  ): React.ReactNode => {
    switch (fieldName) {
      case "usConsentManagementVariantCA":
        return "California";
      case "usConsentManagementVariantUSP":
        return (
          <div
            style={{
              minHeight: "2.5rem",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div>
              <span>Privacy States</span>
              <span className="tw-boundary ml-xs">
                <ToolTip
                  inline
                  text={
                    <>
                      {OVERRIDEABLE_REGIONS.filter(
                        (region) =>
                          region.isUSP && !(region.isChildrenConsent && showMinorsConsentSettings),
                      ).map((region) =>
                        customVariantsUS?.some(
                          (customRegion) => customRegion.region == region.region,
                        ) ? (
                          <div key={region.label} className="text-gray-400">
                            {region.label}
                          </div>
                        ) : (
                          <div key={region.label}>{region.label}</div>
                        ),
                      )}
                    </>
                  }
                  xOffset={96}
                  yOffset={148}
                >
                  <small className="text-blue-600 cursor-pointer">See States</small>
                </ToolTip>
              </span>
            </div>
            {customVariantsUS?.some((item) =>
              OVERRIDEABLE_REGIONS.some(
                (state) =>
                  state.isUSP &&
                  !(state.isChildrenConsent && showMinorsConsentSettings) &&
                  state.region == item.region,
              ),
            ) && <small>Some states have been overridden below</small>}
          </div>
        );
      case "usConsentManagementVariantNonUSP":
        return (
          <div
            style={{
              minHeight: "3rem",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div>
              <span>Non-Privacy States</span>
              <span className="tw-boundary ml-xs">
                <ToolTip
                  inline
                  text={
                    <>
                      {OVERRIDEABLE_REGIONS.filter(
                        (region) =>
                          region.isNonUSP &&
                          !(region.isChildrenConsent && showMinorsConsentSettings),
                      ).map((region) =>
                        customVariantsUS?.some(
                          (customRegion) => customRegion.region == region.region,
                        ) ? (
                          <div key={region.label} className="text-gray-400">
                            {region.label}
                          </div>
                        ) : (
                          <div key={region.label}>{region.label}</div>
                        ),
                      )}
                    </>
                  }
                  xOffset={96}
                  yOffset={384}
                >
                  <small className="text-blue-600 cursor-pointer">See States</small>
                </ToolTip>
              </span>
            </div>
            {customVariantsUS?.some((item) =>
              OVERRIDEABLE_REGIONS.some(
                (state) =>
                  state.isNonUSP &&
                  !(state.isChildrenConsent && showMinorsConsentSettings) &&
                  state.region == item.region,
              ),
            ) && <small>Some states have been overridden below</small>}
          </div>
        );
      case "usConsentManagementVariantMinors":
        return (
          <div
            style={{
              minHeight: "4.5rem",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div>
              <span>
                Children Consent States (under{" "}
                {props.orgInfo?.showMinorsConsentSettingsUnder16 === true ? "16" : "18"})
              </span>
              <span className="tw-boundary ml-xs">
                <ToolTip
                  inline
                  text={
                    <>
                      {OVERRIDEABLE_REGIONS.filter(
                        (region) =>
                          region.isChildrenConsent && region.dataCollectionAge >= dataCollectionAge,
                      ).map((region) =>
                        customVariantsUS?.some(
                          (customRegion) => customRegion.region == region.region,
                        ) ? (
                          <div key={region.label} className="text-gray-400">
                            {region.label}
                            {region.dataCollectionAge !== 18
                              ? ` (under ${region.dataCollectionAge})`
                              : null}
                          </div>
                        ) : (
                          <div key={region.label}>
                            {region.label}
                            {region.dataCollectionAge !== 18
                              ? ` (under ${region.dataCollectionAge})`
                              : null}
                          </div>
                        ),
                      )}
                    </>
                  }
                  xOffset={96}
                  yOffset={96}
                >
                  <small className="text-blue-600 cursor-pointer">See States</small>
                </ToolTip>
              </span>
            </div>
            <small
              className="block max-w-[250px]"
              style={{ whiteSpace: "initial", lineHeight: "1rem" }}
            >
              Because you collect children's data, some states require you to collect consent.
            </small>
            {customVariantsUS?.some((item) =>
              OVERRIDEABLE_REGIONS.some(
                (state) => state.isChildrenConsent && state.region == item.region,
              ),
            ) && <small>Some states have been overridden below</small>}
          </div>
        );
    }
  };

  const [confirmClearUSCustomRegionsDialogOpen, setConfirmClearUSCustomRegionsDialogOpen] =
    useState(false);

  const developerPreviewURL = isStagingEnvironment()
    ? `https://staging.polaris.truevaultcdn.com/static/pc/${props.privacyCenterPublicId}/staging/polaris.js`
    : `https://polaris.truevaultcdn.com/static/pc/${props.privacyCenterPublicId}/staging/polaris.js`;
  const [isSavingDeveloperPreview, setIsSavingDeveloperPreview] = useState(false);

  return (
    <>
      <Form
        initialValues={{
          usConsentManagementVariant: props.usConsentManagementVariant,
          usConsentManagementVariantCA: props.usConsentManagementVariantCA,
          usConsentManagementVariantUSP: props.usConsentManagementVariantUSP,
          usConsentManagementVariantNonUSP: props.usConsentManagementVariantNonUSP,
          usConsentManagementVariantMinors: props.usConsentManagementVariantMinors,
          canadaConsentManagementVariant: props.canadaConsentManagementVariant,
          eeaukConsentManagementVariant: props.eeaukConsentManagementVariant,
          customConsentManagementVariants: props.customConsentManagementVariants,
          bannerIconColorPrimary: props.bannerIconColorPrimary || ICON_PRIMARY_COLOR_DEFAULT,
          bannerIconColorSecondary: props.bannerIconColorSecondary || ICON_SECONDARY_COLOR_DEFAULT,
          bannerBackgroundColor: props.bannerBackgroundColor,
          bannerScreenPosition: props.bannerScreenPosition,
          informationalBannerTitle:
            props.informationalBannerTitle || INFORMATIONAL_BANNER_TITLE_DEFAULT_TEXT,
          informationalConsentManagementVariant:
            props.informationalConsentManagementVariant === true,
          informationalBannerText: informationalBannerInitialText,
          informationalBannerAcceptButtonText:
            props.informationalBannerAcceptButtonText ||
            INFORMATIONAL_BANNER_ACCEPT_BUTTON_DEFAULT_TEXT,
          informationalBannerDismissalStyle:
            props.informationalBannerDismissalStyle ||
            InformationalBannerDismissalStyle.ACCEPT_BUTTON,
          bannerTextOptInEn: props.bannerTextOptInEn,
          bannerTextOptOutEn: props.bannerTextOptOutEn,
          bannerCopyVariant: props.bannerCopyVariant || BannerCopyVariant.DEFAULT,
          gdprConsentManagementEnabled: props.gdprConsentManagementEnabled,
          previewURL: props.previewURL ?? tryExtractWebsiteURLfromPCURL(props.privacyCenterURL),
          consentRequired: props.consentRequiredMode === ConsentRequiredMode.EVERYWHERE,
          allowCSSOverrides: props.allowCSSOverrides === true,
        }}
        onSubmit={async (values) => {
          values.previewForceGeoCountry = null; // Publishing, clear out the preview settings
          values.previewForceGeoRegion = null;
          values.isStagingPublish = false;

          // --- See if we are trying to publish without having made an explicit choice for an available banner variant.
          const message =
            "Please select a banner variant for remaining regions before publishing: ";
          const missingVariants = [];
          if (!values.usConsentManagementVariantCA && showCalifornia) {
            missingVariants.push("California");
          }
          if (!values.usConsentManagementVariantUSP) {
            missingVariants.push("Privacy States");
          }
          if (!values.usConsentManagementVariantNonUSP) {
            missingVariants.push("Non-Privacy States");
          }
          if (!values.usConsentManagementVariantMinors && showMinorsConsentSettings) {
            missingVariants.push("Children Consent States");
          }
          if (!values.canadaConsentManagementVariant && showCanadaBannerSettings) {
            missingVariants.push("Canada");
          }
          if (!values.eeaukConsentManagementVariant && showGDPRBannerSettings) {
            missingVariants.push("EEA/UK/CH");
          }

          if (missingVariants.length !== 0) {
            enqueueSnackbar(message + missingVariants.join(", "), {
              variant: "error",
            });

            return { [FORM_ERROR]: message + missingVariants.join(", ") };
          }
          // --- End of missing variants check.

          // Re-map checkbox to enum
          values.consentRequiredMode = values.consentRequired
            ? ConsentRequiredMode.EVERYWHERE
            : ConsentRequiredMode.NONE;

          // Set nulls to "" so the backend patch DTO sees them for the update. The CMP uses the default in this case.
          if (!values.bannerTextOptInEn) {
            values.bannerTextOptInEn = "";
          }
          if (!values.bannerTextOptOutEn) {
            values.bannerTextOptOutEn = "";
          }
          if (!values.bannerCopyVariant) {
            values.bannerCopyVariant = BannerCopyVariant.DEFAULT;
          }

          try {
            await props.onUpdateCMPCustomization(values as CMPSettingsDto);
          } catch (error) {
            enqueueSnackbar("Failed to publish consent banner settings. Please try again.", {
              variant: "error",
            });
            return { [FORM_ERROR]: "Failed to publish consent banner settings" };
          }
        }}
        validate={(values) => {
          const errors = {};
          requiredString(values, "informationalBannerText", errors);
          requiredString(values, "informationalBannerAcceptButtonText", errors);
          return errors;
        }}
        render={({ handleSubmit, form, dirty, dirtyFields: _dirtyFields, hasValidationErrors }) => {
          // Store the handleSubmit function in the ref so parent can call it
          submitHandlerRef.current = handleSubmit;

          const usVariantCA = form.getFieldState("usConsentManagementVariantCA")?.value;
          const usVariantUSP = form.getFieldState("usConsentManagementVariantUSP")?.value;
          const usVariantNonUSP = form.getFieldState("usConsentManagementVariantNonUSP")?.value;
          const usVariantMinors = form.getFieldState("usConsentManagementVariantMinors")?.value;
          const customVariantsUS: CustomConsentManagementVariant[] = form.getFieldState(
            "customConsentManagementVariants",
          )?.value;

          const hasUSInformationalBanner =
            [usVariantCA, usVariantUSP, usVariantNonUSP, usVariantMinors].some(
              (value) => value === ConsentManagementVariant.INFORMATIONAL_ONLY,
            ) ||
            customVariantsUS?.some(
              (item) => item.variant === ConsentManagementVariant.INFORMATIONAL_ONLY,
            );
          const hasUSOptOutBanner =
            [usVariantCA, usVariantUSP, usVariantNonUSP, usVariantMinors].some(
              (value) => value === ConsentManagementVariant.INTERACTIVE_DEFAULT_CONSENT,
            ) ||
            customVariantsUS?.some(
              (item) => item.variant === ConsentManagementVariant.INTERACTIVE_DEFAULT_CONSENT,
            );

          const hasUSOptInBanner =
            [usVariantCA, usVariantUSP, usVariantNonUSP, usVariantMinors].some(
              (value) => value === ConsentManagementVariant.INTERACTIVE_DEFAULT_NO_CONSENT,
            ) ||
            customVariantsUS?.some(
              (item) => item.variant === ConsentManagementVariant.INTERACTIVE_DEFAULT_NO_CONSENT,
            );

          const hasUSInteractiveBanner =
            [usVariantCA, usVariantUSP, usVariantNonUSP, usVariantMinors].some((value) =>
              [
                ConsentManagementVariant.INTERACTIVE_DEFAULT_CONSENT,
                ConsentManagementVariant.INTERACTIVE_DEFAULT_NO_CONSENT,
              ].includes(value),
            ) ||
            customVariantsUS?.some(
              (item) =>
                item.variant === ConsentManagementVariant.INTERACTIVE_DEFAULT_CONSENT ||
                item.variant === ConsentManagementVariant.INTERACTIVE_DEFAULT_NO_CONSENT,
            );

          const canadaVariant = form.getFieldState("canadaConsentManagementVariant")?.value;
          const eeaukVariant = form.getFieldState("eeaukConsentManagementVariant")?.value;

          const usBannerEnabled = [
            usVariantCA,
            usVariantUSP,
            usVariantNonUSP,
            usVariantMinors,
          ].some((value) => value && value !== "NONE");
          const canadaBannerEnabled =
            showCanadaBannerSettings && canadaVariant && canadaVariant !== "NONE";
          const eeaukBannerEnabled =
            showGDPRBannerSettings && eeaukVariant && eeaukVariant !== "NONE";
          const showBannerVisualSettings =
            usBannerEnabled || canadaBannerEnabled || eeaukBannerEnabled;

          const hasOptInBanner =
            canadaBannerEnabled ||
            [usVariantCA, usVariantUSP, usVariantNonUSP].some(
              (value) => value === ConsentManagementVariant.INTERACTIVE_DEFAULT_NO_CONSENT,
            ) ||
            customVariantsUS?.some(
              (item) => item.variant === ConsentManagementVariant.INTERACTIVE_DEFAULT_NO_CONSENT,
            );
          const hasOptOutBanner =
            canadaBannerEnabled ||
            [usVariantCA, usVariantUSP, usVariantNonUSP].some(
              (value) => value === ConsentManagementVariant.INTERACTIVE_DEFAULT_CONSENT,
            ) ||
            customVariantsUS?.some(
              (item) => item.variant === ConsentManagementVariant.INTERACTIVE_DEFAULT_CONSENT,
            );

          const showUSCallout = !hasCookies && hasUSInteractiveBanner;
          const showCanadaCallout =
            (canadaVariant === ConsentManagementVariant.INTERACTIVE_DEFAULT_CONSENT ||
              canadaVariant === ConsentManagementVariant.INTERACTIVE_DEFAULT_NO_CONSENT) &&
            !hasCookies;
          const showEEAUKCallout =
            (eeaukVariant === ConsentManagementVariant.INTERACTIVE_DEFAULT_CONSENT ||
              eeaukVariant === ConsentManagementVariant.INTERACTIVE_DEFAULT_NO_CONSENT) &&
            !hasCookies;

          const handleCustomVariantsUpdate = (
            updatedVariants: CustomConsentManagementVariant[],
          ) => {
            // FUTURE: If we ever have more than one custom variants table (e.g. region list outside of US), need to merge
            // `updatedVariants` with what currently exists in `customConsentManagementVariants` instead of overwriting.
            form.change("customConsentManagementVariants", updatedVariants);
          };

          return (
            <>
              <ConfirmDialog
                open={confirmClearUSCustomRegionsDialogOpen}
                onClose={() => setConfirmClearUSCustomRegionsDialogOpen(false)}
                onCancel={() => setConfirmClearUSCustomRegionsDialogOpen(false)}
                onConfirm={() => {
                  form.change("customConsentManagementVariants", []);
                  setShowUSOverrides(false);
                  setConfirmClearUSCustomRegionsDialogOpen(false);
                }}
                confirmTitle="Clear Custom US States"
                contentText="Are you sure you want to clear all consent settings for custom US states?"
              />
              {dirty && !hasValidationErrors && (
                <UnpublishedChangesBanner
                  disabled={!dirty || hasValidationErrors || loadingPreview}
                  onPublish={(e?: any) => handleSubmit(e)}
                />
              )}
              {!dirty && props.saveSuccess && props.isConfirmedCustomizableCMPVersion && (
                <div style={{ backgroundColor: "#E2FDF3", padding: "16px", color: "#00331F" }}>
                  Your changes were successfully published.
                </div>
              )}
              {!dirty && props.saveSuccess && !props.isConfirmedCustomizableCMPVersion && (
                <div style={{ backgroundColor: "#FBF8D0", padding: "16px", color: "#4D260E" }}>
                  Your changes were successfully published, but we still haven't been able to verify
                  that your site is using the latest Polaris JavaScript. The latest JavaScript is
                  required for the changes to be visible.
                </div>
              )}
              <hr className="mb-md" />
              <div className="max-width-896">
                <ConsentManagementVariantRow
                  title="US Banner"
                  helpText="US privacy laws do not require a cookie banner, but many businesses choose to offer consent choices for various reasons, including to minimize data privacy risks."
                  fields={[
                    ...(showCalifornia ? ["usConsentManagementVariantCA"] : []),
                    ...(showMinorsConsentSettings ? ["usConsentManagementVariantMinors"] : []),
                    "usConsentManagementVariantUSP",
                    "usConsentManagementVariantNonUSP",
                  ]}
                  allowInformationalBanner={true}
                  allowOptOutBanner={true}
                  allowOptInBanner={true}
                  iconPath="/assets/images/flag-usa-small.png"
                  showTooltip={true}
                  fieldToLabel={FORM_FIELDS_TO_HUMAN_DESCRIPTION}
                  reverseOptionOrder
                  verticalLayout
                  fieldDescriptionToRowLabelProvider={(field: string) => {
                    return fieldDescriptionToRowLabelProvider(field, customVariantsUS);
                  }}
                  contentBeforeTable={
                    hasUSOptInBanner ? (
                      <div className="mb-sm max-width-896">
                        <AlertCard
                          variant="info"
                          containerClasses="mt-0 mb-0"
                          title="Opt-In Enabled: Non-essential cookies and scripts are turned off by default with an opt-in banner. You may notice a drop in measurements, such as website analytics."
                        />
                      </div>
                    ) : null
                  }
                />
                <div>
                  <small className="d-flex align-items-center" style={{ gap: "0.25em" }}>
                    <strong>Advanced Banner Visibility Options</strong>
                    <span className="text-muted">
                      (Choices will override default banner visibility for selected states)
                    </span>
                    <span
                      className="tw-boundary"
                      style={{ marginTop: "0.25rem", marginLeft: "0.25rem" }}
                    >
                      <Toggle
                        checked={showUSOverrides}
                        onChange={(checked) => {
                          if (!checked && customVariantsUS?.length > 0) {
                            setConfirmClearUSCustomRegionsDialogOpen(true);
                          } else {
                            setShowUSOverrides(checked);
                          }
                        }}
                      />
                    </span>
                  </small>
                </div>
                {showUSOverrides && (
                  <ConsentManagementVariantCustomRow
                    tableHeader="US States Overrides"
                    customVariants={customVariantsUS}
                    allowedRegions={OVERRIDEABLE_REGIONS}
                    onUpdate={handleCustomVariantsUpdate}
                  />
                )}
                {hasUSInformationalBanner && (
                  <p className="text-t1 text-muted mt-xs">
                    Note that an informational privacy banner will not affect the use of cookies or
                    trackers on your site, and should not be used as a replacement for collecting
                    affirmative consent from visitors where that is required.
                  </p>
                )}
                {showUSCallout && <NoCookiesCallout />}
                {showCanadaBannerSettings && (
                  <>
                    <hr className="my-lg" />
                    <ConsentManagementVariantRow
                      title="Canada Banner"
                      helpText="Canada's PIPEDA law requires that websites give visitors the choice to withdraw consent for data processing (Opt-Out Banner)."
                      fields={["canadaConsentManagementVariant"]}
                      recommendedValue={"3"}
                      allowInformationalBanner={false}
                      allowOptOutBanner={false}
                      allowOptInBanner={true}
                      useOnOffPhrasing
                      iconPath="/assets/images/flag-canada.svg"
                    />
                    {showCanadaCallout && <NoCookiesCallout />}
                  </>
                )}
                {showGDPRBannerSettings && (
                  <>
                    <hr className="my-lg" />
                    <ConsentManagementVariantRow
                      title="EEA/UK/CH Banner"
                      helpText="The European e-privacy law requires businesses to obtain consent before collecting data via web cookies (Opt-In Banner)."
                      fields={["eeaukConsentManagementVariant"]}
                      recommendedValue={"3"}
                      allowInformationalBanner={false}
                      allowOptOutBanner={false}
                      allowOptInBanner={true}
                      useOnOffPhrasing
                      iconPath="/assets/images/flag-eea.svg"
                    />
                    {showEEAUKCallout && <NoCookiesCallout />}
                  </>
                )}
                {(hasUSInteractiveBanner || canadaBannerEnabled || eeaukBannerEnabled) && (
                  <>
                    <hr className="my-lg" />
                    <ConsentBannerBackgroundColorRow />
                    <hr className="my-lg" />
                    <ConsentBannerIconColorRow
                      usBannerEnabled={hasUSInteractiveBanner}
                      canadaBannerEnabled={canadaBannerEnabled}
                      eeaukBannerEnabled={eeaukBannerEnabled}
                      formApi={form as any}
                    />
                    <hr className="my-lg" />
                    <ConsentBannerScreenPositionRow />
                  </>
                )}
                {hasUSInteractiveBanner && (
                  <>
                    <hr className="my-lg" />
                    <ConsentBannerUSTitleRow formApi={form} />
                  </>
                )}
                {hasUSInformationalBanner && (
                  <>
                    <hr className="my-lg" />
                    <ConsentBannerUSTextRow
                      formApi={form}
                      informationalBannerDefaultText={informationalBannerDefaultText}
                    />
                    <hr className="my-lg" />
                    <ConsentBannerUSDismissalRow />
                  </>
                )}
                {hasOptOutBanner && (
                  <>
                    <hr className="my-lg" />
                    <ConsentBannerCopyOverrideRow
                      formApi={form as any}
                      title="Customize Opt-Out Banner Text"
                      description="This is the text that will show on the English Opt-Out banner. We recommend the default text, but you may customize it to take full ownership of your on-page disclosure."
                      field="bannerTextOptOutEn"
                      resetText={optOutBannerDefaultText}
                      startToggled={props.bannerTextOptOutEn && props.bannerTextOptOutEn !== ""}
                    />
                  </>
                )}
                {hasOptInBanner && (
                  <>
                    <hr className="my-lg" />
                    <ConsentBannerCopyOverrideRow
                      formApi={form as any}
                      title="Customize Opt-In Banner Text"
                      description="This is the text that will show on the English Opt-In banner. We recommend the default text, but you may customize it to take full ownership of your on-page disclosure."
                      field="bannerTextOptInEn"
                      resetText={optInBannerDefaultText}
                      startToggled={props.bannerTextOptInEn && props.bannerTextOptInEn !== ""}
                    />
                  </>
                )}
                {(hasUSInformationalBanner || hasUSOptOutBanner) && (
                  <>
                    <hr className="my-lg" />
                    <ConsentBannerUSButtonRow formApi={form} />
                  </>
                )}
                {hasOptInBanner && (
                  <>
                    <hr className="my-lg" />
                    <ConsentBannerOptInButtonTextRow />
                  </>
                )}
                {showBannerVisualSettings && (
                  <>
                    <hr className="my-lg" />
                    <ConsentBannerConsentRequiredRow />
                  </>
                )}
                {showBannerVisualSettings && (
                  <>
                    <hr className="my-lg" />
                    <ConsentBannerAllowCSSOverridesRow />
                  </>
                )}
                {showBannerVisualSettings &&
                  (loadingPreview ? (
                    <>
                      <hr className="my-lg" />
                      <ZeroStateHero
                        text={
                          previewReady
                            ? `Your consent banner preview is ready to view.`
                            : `Hang tight while we cook up a fresh preview for you! (${loadingPreviewProgress})`
                        }
                        title={previewReady ? "Preview Ready!" : "Preparing Preview"}
                        ctaButtonText={previewReady ? "View Preview" : null}
                        ctaButtonOnClick={
                          previewReady
                            ? () => {
                                window.open(
                                  `https://${bannerPreviewURL}#polaris_preview=true`,
                                  "_blank",
                                );
                                enqueueSnackbar("Preview opened in new window!", {
                                  variant: "success",
                                });
                                setLoadingPreview(false);
                                setPreviewReady(false);
                              }
                            : null
                        }
                      ></ZeroStateHero>
                      {!previewReady && <Loading />}
                    </>
                  ) : (
                    <>
                      <hr className="my-lg" />
                      <div className="mt-sm mb-sm">
                        <ConsentBannerPreviewURLRow formApi={form as any} />
                      </div>
                      <SettingRow
                        maxWidthChildren={true}
                        title="Preview"
                        helpText="Click a region to see a preview of your selected banner."
                      >
                        <div className="flex flex-row align-items-left mt-lg">
                          <div className="mr-lg">
                            <Tooltip
                              title={
                                usBannerEnabled
                                  ? "US - California"
                                  : "US (Enable a US banner to preview)"
                              }
                            >
                              <img
                                src="/assets/images/flag-usa-small.png"
                                alt="US - California"
                                width="50"
                                height="33"
                                onClick={() => {
                                  if (!usBannerEnabled) {
                                    return;
                                  }
                                  publishPreview("UNITED_STATES", form.getState().values);
                                }}
                                className={clsx("box-rounded", {
                                  "opacity-50": !usBannerEnabled,
                                  "cursor-pointer": usBannerEnabled,
                                })}
                                style={{ filter: !usBannerEnabled ? "grayscale(1)" : "initial" }}
                              />
                            </Tooltip>
                          </div>
                          {showCanadaBannerSettings && (
                            <div className="mr-lg">
                              <Tooltip
                                title={
                                  canadaBannerEnabled
                                    ? "Canada"
                                    : "Canada (Enable a Canadian banner to preview)"
                                }
                              >
                                <img
                                  src="/assets/images/flag-canada.svg"
                                  alt="Canada"
                                  width="50"
                                  onClick={() => {
                                    if (!canadaBannerEnabled) {
                                      return;
                                    }
                                    publishPreview("CANADA", form.getState().values);
                                  }}
                                  className={clsx("box-rounded", {
                                    "opacity-50": !canadaBannerEnabled,
                                    "cursor-pointer": canadaBannerEnabled,
                                  })}
                                  style={{
                                    filter: !canadaBannerEnabled ? "grayscale(1)" : "initial",
                                  }}
                                />
                              </Tooltip>
                            </div>
                          )}
                          {showGDPRBannerSettings && (
                            <div>
                              <Tooltip
                                title={
                                  eeaukBannerEnabled
                                    ? "EEA/UK/CH"
                                    : "EEA/UK/CH (Enable an EEA/UK/CH banner to preview)"
                                }
                              >
                                <img
                                  src="/assets/images/flag-eea.svg"
                                  alt="EEA/UK/CH"
                                  width="50"
                                  onClick={() => {
                                    if (!eeaukBannerEnabled) {
                                      return;
                                    }
                                    publishPreview("EEA_UK", form.getState().values);
                                  }}
                                  className={clsx("box-rounded", {
                                    "opacity-50": !eeaukBannerEnabled,
                                    "cursor-pointer": eeaukBannerEnabled,
                                  })}
                                  style={{
                                    filter: !eeaukBannerEnabled ? "grayscale(1)" : "initial",
                                  }}
                                />
                              </Tooltip>
                            </div>
                          )}
                        </div>
                      </SettingRow>
                      <hr className="my-lg" />
                      <SettingRow
                        maxWidthChildren={true}
                        title="Staging Banner"
                        helpText="You can test changes by publishing a staging banner to use on your staging website."
                      >
                        <ol className="custom custom-staging p-0">
                          {/* Step 1 */}
                          <li className="d-flex align-items-start mb-md">
                            <div style={{ fontSize: "16px", color: "#1A1726" }}>
                              Follow the{" "}
                              <Link
                                style={{ textDecoration: "underline", color: "#1A1726" }}
                                href={RouteHelpers.instructions.id(organization?.devInstructionsId)}
                                target="_blank"
                              >
                                Developer Instructions
                              </Link>
                              , using the below Staging JavaScript in place of the JavaScript in
                              Step 2 of Web Notices. It may take a few minutes for changes to appear
                              on your staging site after publishing.
                            </div>
                          </li>

                          {/* Step 2 */}
                          <li className="d-flex align-items-start mb-md">
                            <div className="tw-boundary">
                              <span
                                style={{ fontSize: "16px", color: "#1A1726", fontWeight: "bold" }}
                              >
                                JavaScript:
                              </span>{" "}
                              <CopyableText
                                longText={developerPreviewURL}
                                shortText={developerPreviewURL}
                              />
                            </div>
                          </li>

                          {/* Step 3 */}
                          <li className="d-flex align-items-center">
                            <div className="tw-boundary d-flex align-items-center">
                              <AquiloButton
                                color="tertiary"
                                size="xs"
                                disabled={isSavingDeveloperPreview}
                                onClick={async () => {
                                  setIsSavingDeveloperPreview(true);
                                  await publishStaging(form.getState().values);
                                  setTimeout(() => {
                                    setIsSavingDeveloperPreview(false);
                                  }, 4000);
                                }}
                              >
                                Publish Settings to Staging Javascript
                              </AquiloButton>
                              {isSavingDeveloperPreview && (
                                <>
                                  <DoneIcon className="mr-xxs ml-xs" style={{ width: "18px" }} />
                                  <span>
                                    <small>Published Staging</small>
                                  </span>
                                </>
                              )}
                            </div>
                          </li>
                        </ol>
                      </SettingRow>
                    </>
                  ))}
              </div>
              {/* workaround for React Final Form tree-shaking the field since it doesn't think it's used unless it's named by a form component */}
              <div style={{ display: "none" }}>
                <Text field="customConsentManagementVariants" />
              </div>
            </>
          );
        }}
      />
    </>
  );
});

interface UseConsentBannerSettings {
  settings: CMPSettingsDto;
  privacyCenter: PrivacyCenterDto;
  privacyCenterRequest: NetworkRequestState;
  cmpCustomizationRequest: NetworkRequestState;

  getLatestData: () => void;
}

export const useConsentBannerSettings = (): UseConsentBannerSettings => {
  const organizationId = usePrimaryOrganizationId();

  const [privacyCenter, privacyCenterRequest, _fetch] = usePrivacyCenter(organizationId);
  const [cmpCustomization, cmpCustomizationRequest, getLatestData] = useCMPCustomization(
    organizationId,
    privacyCenter?.id,
  );

  return {
    settings: {
      informationalConsentManagementVariant:
        cmpCustomization?.informationalConsentManagementVariant === true,
      informationalBannerAcceptButtonText: cmpCustomization?.informationalBannerAcceptButtonText,
      informationalBannerDismissalStyle: cmpCustomization?.informationalBannerDismissalStyle,
      informationalBannerText: cmpCustomization?.informationalBannerText,
      informationalBannerTitle: cmpCustomization?.informationalBannerTitle,
      bannerTextOptInEn: cmpCustomization?.bannerTextOptInEn,
      bannerTextOptOutEn: cmpCustomization?.bannerTextOptOutEn,
      bannerCopyVariant: cmpCustomization?.bannerCopyVariant,

      usConsentManagementVariant: cmpCustomization?.usConsentManagementVariant,
      usConsentManagementVariantCA: cmpCustomization?.usConsentManagementVariantCA,
      usConsentManagementVariantUSP: cmpCustomization?.usConsentManagementVariantUSP,
      usConsentManagementVariantNonUSP: cmpCustomization?.usConsentManagementVariantNonUSP,
      usConsentManagementVariantMinors: cmpCustomization?.usConsentManagementVariantMinors,
      eeaukConsentManagementVariant: cmpCustomization?.eeaukConsentManagementVariant,
      canadaConsentManagementVariant: cmpCustomization?.canadaConsentManagementVariant,
      customConsentManagementVariants: cmpCustomization?.customConsentManagementVariants,

      usBannerVisibility: cmpCustomization?.usBannerVisibility,

      bannerBackgroundColor: cmpCustomization?.bannerBackgroundColor,
      bannerIconColorPrimary: cmpCustomization?.bannerIconColorPrimary,
      bannerIconColorSecondary: cmpCustomization?.bannerIconColorSecondary,

      bannerScreenPosition: cmpCustomization?.bannerScreenPosition,

      gdprConsentManagementEnabled: cmpCustomization?.gdprConsentManagementEnabled,
      previewURL: cmpCustomization?.previewURL,

      consentRequiredMode: cmpCustomization?.consentRequiredMode,
      allowCSSOverrides: cmpCustomization?.allowCSSOverrides,

      orgInfo: cmpCustomization?.orgInfo,
    },
    privacyCenter,
    privacyCenterRequest,
    cmpCustomizationRequest,
    getLatestData,
  };
};
